import {
  CampaignTemplateChannel,
  CampaignTemplateType,
  ICampaignTemplate,
} from '@dataTypes/marketing.type'
import {
  deleteCampaignTemplate,
  setShowTemplatePreview,
  setTemplate,
} from '@store/actions'
import { setTemplatesListChannel } from '@store/reducers/marketing.reducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { MenuEventArgs } from '@syncfusion/ej2-react-navigations'
import { useEffect, useState } from 'react'

interface TemplateListHooks {
  activeChannel: string
  builtInTemplates: ICampaignTemplate[]
  handleChangeChannel: (channel: string) => void
  handleCreateNewTemplate: () => void
  handleDeleteTemplate: () => void
  handleDropdownClick: (template: ICampaignTemplate, e: MenuEventArgs) => void
  handleSelectTemplate: (template: ICampaignTemplate) => void
  onHideDeleteModal: () => void
  savedTemplates: ICampaignTemplate[]
  selectedTemplate: ICampaignTemplate | null
  showDeleteModal: boolean
}

const useTemplateList = (): TemplateListHooks => {
  const dispatch = useAppDispatch()
  const selectedTemplate = useAppSelector(
    (state) => state.marketing.selectedTemplate,
  )
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const campaignTemplates = useAppSelector(
    (state) => state.marketing.campaignTemplates,
  )
  const activeChannel = useAppSelector(
    (state) => state.marketing.templatesListChannel,
  )
  const [builtInTemplates, setBuiltInTemplates] = useState<ICampaignTemplate[]>(
    [],
  )
  const [savedTemplates, setSavedTemplates] = useState<ICampaignTemplate[]>([])

  const handleChangeChannel = (channel: string): void => {
    if (channel === 'Email' || channel === 'SMS') {
      dispatch(setTemplatesListChannel(channel))
    }
  }

  const handleSelectTemplate = (template: ICampaignTemplate): void => {
    dispatch(setShowTemplatePreview(true))
    dispatch(setTemplate({ template, type: 'selected' }))
  }

  const handleCreateNewTemplate = (): void => {
    const channel =
      activeChannel === 'Email'
        ? CampaignTemplateChannel.email
        : CampaignTemplateChannel.sms
    const draft: ICampaignTemplate = {
      channel,
      name: '',
      type: CampaignTemplateType.saved,
      text_content: '',
      html_content: '',
      created_by: '',
      date_time_created_at: new Date().toISOString(),
      date_time_updated_at: new Date().toISOString(),
    }
    dispatch(setTemplate({ template: draft, type: 'selected' }))
    dispatch(setShowTemplatePreview(true))
  }

  const handleDropdownClick = (
    template: ICampaignTemplate,
    e: MenuEventArgs,
  ): void => {
    const action = e.item.text?.toLowerCase() || ''
    dispatch(setTemplate({ template, type: 'selected' }))
    if (action === 'edit') {
      dispatch(setShowTemplatePreview(true))
    } else if (action === 'delete') {
      setShowDeleteModal(true)
    }
  }

  const handleDeleteTemplate = (): void => {
    if (selectedTemplate?.campaign_template_id) {
      dispatch(deleteCampaignTemplate(selectedTemplate.campaign_template_id))
      setShowDeleteModal(false)
    }
  }

  const onHideDeleteModal = (): void => {
    setShowDeleteModal(false)
    dispatch(setTemplate({ template: null, type: 'selected' }))
  }

  useEffect(() => {
    const channel =
      activeChannel === 'Email'
        ? CampaignTemplateChannel.email
        : CampaignTemplateChannel.sms

    setBuiltInTemplates(
      campaignTemplates.filter(
        (template) =>
          template.type === CampaignTemplateType.predefined &&
          template.channel === channel,
      ),
    )
    setSavedTemplates(
      campaignTemplates.filter(
        (template) =>
          template.type === CampaignTemplateType.saved &&
          template.channel === channel,
      ),
    )
  }, [campaignTemplates, activeChannel])

  return {
    activeChannel,
    builtInTemplates,
    handleChangeChannel,
    handleCreateNewTemplate,
    handleDeleteTemplate,
    handleDropdownClick,
    handleSelectTemplate,
    onHideDeleteModal,
    savedTemplates,
    selectedTemplate,
    showDeleteModal,
  }
}

export default useTemplateList
